/**
 * RegionsLayer Component
 *
 * Renders wall section region polygons on the gym floor plan map.
 * Used when the map is zoomed out to show region overview before drilling down to pins.
 *
 * Features:
 * - Renders all region polygons with labels
 * - Click/tap handling for region drill-down
 * - Opacity fade transitions for smooth mode switching
 * - Highlight animation on region tap
 *
 * @example
 * ```tsx
 * <RegionsLayer
 *   regions={floorPlan.regions}
 *   imageWidth={1920}
 *   imageHeight={1080}
 *   visible={viewMode === 'regions'}
 *   onRegionClick={(regionName) => zoomToRegion(regionName)}
 * />
 * ```
 */

import { useMemo, useCallback, memo } from 'react';
import { Group } from 'react-konva';
import type Konva from 'konva';
import { WallPolygon, type PolygonColors } from './WallPolygon';
import type { WallSectionRegion } from '@flash/shared';

/**
 * Props for RegionsLayer component
 */
export interface RegionsLayerProps {
  /** Wall section regions to render */
  regions: WallSectionRegion[];
  /** Floor plan image width in pixels */
  imageWidth: number;
  /** Floor plan image height in pixels */
  imageHeight: number;
  /** Whether the layer is visible (controls opacity) */
  visible: boolean;
  /** Called when a region is clicked/tapped */
  onRegionClick?: (regionName: string) => void;
  /** Custom colors for region polygons */
  colors?: Partial<PolygonColors>;
  /** Font size for region labels (default: 16) */
  fontSize?: number;
}

/**
 * Default colors for region polygons (semi-transparent blue)
 */
const DEFAULT_REGION_COLORS: Partial<PolygonColors> = {
  fill: 'rgba(59, 130, 246, 0.25)',
  fillHover: 'rgba(59, 130, 246, 0.4)',
  fillSelected: 'rgba(59, 130, 246, 0.5)',
  fillHighlight: 'rgba(59, 130, 246, 0.6)',
  stroke: 'rgba(59, 130, 246, 0.7)',
  labelColor: '#1e40af',
};

/**
 * Default font size for region labels
 */
const DEFAULT_FONT_SIZE = 16;

/**
 * Convert normalized polygon coordinates (0-1) to pixel coordinates
 */
const denormalizePolygon = (
  polygon: { x: number; y: number }[],
  imageWidth: number,
  imageHeight: number
): number[] => {
  const points: number[] = [];
  for (const point of polygon) {
    points.push(point.x * imageWidth, point.y * imageHeight);
  }
  return points;
};

/**
 * RegionsLayer Component
 *
 * Renders region polygons for the zoomed-out view of the climb map.
 */
const RegionsLayerBase = ({
  regions,
  imageWidth,
  imageHeight,
  visible,
  onRegionClick,
  colors = DEFAULT_REGION_COLORS,
  fontSize = DEFAULT_FONT_SIZE,
}: RegionsLayerProps) => {
  // Merge custom colors with defaults
  const mergedColors = useMemo<Partial<PolygonColors>>(
    () => ({
      ...DEFAULT_REGION_COLORS,
      ...colors,
    }),
    [colors]
  );

  // Handle region click
  const handleRegionClick = useCallback(
    (regionName: string) => (_e: Konva.KonvaEventObject<MouseEvent>) => {
      onRegionClick?.(regionName);
    },
    [onRegionClick]
  );

  // Handle region tap
  const handleRegionTap = useCallback(
    (regionName: string) => (_e: Konva.KonvaEventObject<TouchEvent>) => {
      onRegionClick?.(regionName);
    },
    [onRegionClick]
  );

  // Don't render if no regions
  if (regions.length === 0) {
    return null;
  }

  // Don't render if not visible (hidden)
  // We could use opacity for fade transitions, but Konva Group opacity can be expensive
  // For now, we simply don't render when hidden
  if (!visible) {
    return null;
  }

  return (
    <Group>
      {regions.map((region) => {
        const pixelPoints = denormalizePolygon(region.polygon, imageWidth, imageHeight);

        return (
          <WallPolygon
            key={region.wallSectionName}
            id={`region-${region.wallSectionName}`}
            points={pixelPoints}
            label={region.wallSectionName}
            colors={mergedColors}
            fontSize={fontSize}
            showLabel={true}
            listening={true}
            enableHighlightAnimation={true}
            highlightDuration={150}
            onClick={handleRegionClick(region.wallSectionName)}
            onTap={handleRegionTap(region.wallSectionName)}
          />
        );
      })}
    </Group>
  );
};

// Memoize to prevent unnecessary re-renders
export const RegionsLayer = memo(RegionsLayerBase);

export default RegionsLayer;
